import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/apiClient";
import { Search, Bell, MessageSquare, LogOut, Zap, Sun, Moon } from "lucide-react";
import AIAssistant from "@/components/AIAssistant";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const NAV = [
  { label: "Feed", path: "/feed" },
  { label: "Discover", path: "/discover" },
  { label: "My Hub", path: "/dashboard" },
  { label: "Nutrition", path: "/nutrition" },
  { label: "Form AI", path: "/form-checker" },
  { label: "Community", path: "/community" },
];

export default function AppShell() {
  const { user, logout, switchRole } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, [location.pathname]);

  const isInstructor = user?.role === "instructor";

  const handleRoleToggle = async () => {
    const newRole = isInstructor ? "learner" : "instructor";
    await switchRole(newRole);
    toast.success(`Switched to ${newRole === "instructor" ? "Instructor Studio" : "Learner View"}`);
    navigate(newRole === "instructor" ? "/studio" : "/dashboard");
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/discover?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="min-h-screen bg-[#121212]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#121212]/80 border-b border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <button
            data-testid="logo-home-btn"
            onClick={() => navigate("/feed")}
            className="flex items-center gap-2 shrink-0"
          >
            <Logo size={36} />
            <span className="font-display font-black text-xl tracking-tight text-white hidden sm:block">
              FIT<span className="text-[#FF6B6B]">CONNECT</span>
            </span>
          </button>

          <form onSubmit={onSearch} className="flex-1 max-w-md hidden md:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
              <input
                data-testid="global-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search coaches, plans, disciplines…"
                className="w-full bg-[#1E1E1E] border border-[#2A2A2A] rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B] transition-colors"
              />
            </div>
          </form>

          <nav className="hidden lg:flex items-center gap-1 ml-auto">
            {NAV.map((n) => (
              <button
                key={n.path}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => navigate(n.path)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  location.pathname === n.path
                    ? "text-[#FF6B6B] bg-[#FF6B6B]/10"
                    : "text-[#A0A0A0] hover:text-white"
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 ml-auto lg:ml-2">
            {/* Role toggle */}
            <button
              data-testid="role-toggle-switch"
              onClick={handleRoleToggle}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2A2A2A] bg-[#1E1E1E] hover:border-[#FF6B6B]/50 transition-colors"
              title="Switch role"
            >
              <Zap className={`w-3.5 h-3.5 ${isInstructor ? "text-[#4ECCD3]" : "text-[#FF6B6B]"}`} />
              <span className="text-xs font-semibold uppercase tracking-wide text-white">
                {isInstructor ? "Coach" : "Learner"}
              </span>
            </button>

            <button
              data-testid="messages-icon-btn"
              onClick={() => navigate("/messages")}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#A0A0A0] hover:text-white hover:bg-[#1E1E1E] transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
            </button>

            <button
              data-testid="theme-toggle-btn"
              onClick={toggle}
              title="Toggle light / dark"
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#A0A0A0] hover:text-white hover:bg-[#1E1E1E] transition-colors"
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            <div className="relative">
              <button
                data-testid="notifications-bell-btn"
                onClick={() => setShowNotifs((s) => !s)}
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#A0A0A0] hover:text-white hover:bg-[#1E1E1E] transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {notifs.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FF6B6B]" />
                )}
              </button>
              {showNotifs && (
                <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 bg-[#1E1E1E] border border-[#2A2A2A] rounded-xl p-2 shadow-2xl">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A0A0A0] px-2 py-2">Notifications</p>
                  {notifs.length === 0 && <p className="text-sm text-[#A0A0A0] px-2 py-4">No notifications yet.</p>}
                  {notifs.map((n) => (
                    <div key={n.notif_id} className="px-2 py-2 rounded-lg hover:bg-[#2A2A2A] text-sm text-white">
                      {n.text}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="profile-avatar-btn"
              onClick={() => navigate(isInstructor ? "/studio" : "/dashboard")}
              className="w-9 h-9 rounded-full overflow-hidden border border-[#2A2A2A] hover:border-[#FF6B6B] transition-colors"
            >
              <img src={user?.picture} alt={user?.name} className="w-full h-full object-cover" />
            </button>

            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#A0A0A0] hover:text-[#FF3B30] transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 relative z-10">
        <Outlet />
      </main>

      <AIAssistant />
    </div>
  );
}
